import * as React from 'react';
import Drawer from '@mui/material/Drawer';
import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  DialogContentText,
  Snackbar,
} from '@mui/material';
import autobind from 'autobind-decorator';
import { observer, inject } from 'mobx-react';
import { observable } from 'mobx';
import { styled } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import DeleteIcon from '@mui/icons-material/Delete';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import AccountService, { ClinicMetadata } from '../../service/AccountService';
import DeleteDialog, { DeleteClinicDialogState } from './DeleteDialog';

export class SideDrawerState {
  @observable
  isOpen: boolean;

  constructor(value = false) {
    this.isOpen = value;
  }

  setOpen(value: boolean) {
    this.isOpen = value;
  }
}

export type SideDrawerProps = {
  state: SideDrawerState;
  account?: AccountService;
  onAddClinicClicked: () => void;
};

const ListContainer = styled('div')({
  width: 250,
});

@inject('account')
@observer
export default class SideDrawer extends React.Component<
  SideDrawerProps,
  { currentClinicId: string; deleteDialogOpen: boolean }
> {
  state = {
    currentClinicId: '',
    deleteDialogOpen: false,
  };
  _deleteClinicDialogState = new DeleteClinicDialogState();

  @observable
  private _isClinicEdit: boolean = false;

  @observable
  private _selectedClinic: ClinicMetadata = null;

  @observable
  private _selectedClinicName: string = null;

  @observable
  snackMessage = '';

  editClinicName(clinic: ClinicMetadata) {
    this._selectedClinic = clinic;
    this._selectedClinicName = clinic.name;
    this._isClinicEdit = true;
  }

  @autobind
  async saveClinicNameChange() {
    let result = await this.props.account.updateClinicName(
      this._selectedClinic.clinicId,
      this._selectedClinicName
    );
    if (result === 'SUCCESS') {
      this.snackMessage = 'SUCCESS: Updated Clinic Name!';
      this._isClinicEdit = false;
      this.props.account.fetchClinicsById();
    } else {
      this.snackMessage = result.toString();
    }
  }

  @autobind
  async deleteClinicHandler(clinicId: string) {
    await this.props.account.deleteClinic(clinicId);
    const clinics = this.props.account.clinics;
    if (clinics.length > 0) {
      const nextClinic = clinics[0];
      this.props.account.setCurrentClinic(nextClinic.clinicId);
    } else {
      this.props.onAddClinicClicked();
    }
    this._deleteClinicDialogState.open = false;
  }

  showDeleteDialog(clinicId: string) {
    this._deleteClinicDialogState.open = true;
    this.setState({ deleteDialogOpen: true, currentClinicId: clinicId });
  }
  componentDidMount() {
    const clinics = this.props.account.clinics;
    if (clinics.length === 0) this.props.onAddClinicClicked();
  }

  render() {
    return (
      <React.Fragment>
        <Drawer
          open={this.props.state.isOpen}
          onClose={() => this.props.state.setOpen(false)}
        >
          <div
            style={{
              height: 'env(safe-area-inset-top)',
              backgroundColor: '#fff',
            }}
          />
          <ListContainer onClick={() => this.props.state.setOpen(false)}>
            <ListItem button onClick={this.props.onAddClinicClicked}>
              <ListItemText primary={'LOCATIONS'} />
              <ListItemIcon style={{ minWidth: 24 }}>
                <LocalHospitalIcon />
              </ListItemIcon>
            </ListItem>
            <Divider />
            <List>
              {this.props.account.clinics.map((clinic, index) => (
                <ListItem
                  button
                  key={index}
                  selected={clinic.clinicId == this.props.account.currentClinic}
                  onClick={() =>
                    this.props.account.setCurrentClinic(clinic.clinicId)
                  }
                >
                  <ListItemText primary={clinic.name} />
                  <IconButton
                    color='inherit'
                    aria-label='menu'
                    style={{ margin: '2px' }}
                    onClick={() => this.editClinicName(clinic)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    color='inherit'
                    aria-label='menu'
                    style={{ margin: '2px' }}
                    onClick={() => this.showDeleteDialog(clinic.clinicId)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItem>
              ))}
            </List>
          </ListContainer>
        </Drawer>
        {this.state.deleteDialogOpen && (
          <DeleteDialog
            onDelete={() =>
              this.deleteClinicHandler(this.state.currentClinicId)
            }
            dialogState={this._deleteClinicDialogState}
          />
        )}
        {this._isClinicEdit ? (
          <Dialog
            open={this._isClinicEdit}
            onClose={() => {
              this._isClinicEdit = false;
            }}
            aria-labelledby='form-dialog-title'
          >
            <DialogTitle id='form-dialog-title'>Edit Flow Name</DialogTitle>
            <DialogContent>
              <DialogContentText>{this._selectedClinic.name}</DialogContentText>
              <TextField
                id='standard-basic'
                label='Location Name'
                value={this._selectedClinicName}
                onChange={(e) => (this._selectedClinicName = e.target.value)}
                fullWidth
              ></TextField>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => (this._isClinicEdit = false)}
                color='primary'
              >
                Cancel
              </Button>
              <Button onClick={this.saveClinicNameChange} color='primary'>
                Save Location Name
              </Button>
            </DialogActions>
          </Dialog>
        ) : null}
        <Snackbar
          open={this.snackMessage ? true : false}
          autoHideDuration={6000}
          onClose={() => (this.snackMessage = null)}
        >
          <div
            style={{
              backgroundColor: 'orange',
              color: 'white',
              padding: '10px',
              borderRadius: '5px',
            }}
          >
            {this.snackMessage}
          </div>
          {/* <MuiAlert elevation={6} variant="filled" severity="info" onClose={()=>this.snackMessage=null}>{this.snackMessage}</MuiAlert> */}
        </Snackbar>
      </React.Fragment>
    );
  }
}
